import * as Sentry from '@sentry/react';

import { retryableGet } from './reportPageError';

const TIMEOUT_MS = 30_000;

function signal(caller?: AbortSignal | null): AbortSignal {
  const timeout = AbortSignal.timeout(TIMEOUT_MS);
  if (!caller || typeof AbortSignal.any !== 'function') return timeout;
  return AbortSignal.any([caller, timeout]);
}

function retry(failureCount: number, error: unknown): boolean {
  if (failureCount >= 2) return false;
  const resolved = retryableGet(error);
  if (!resolved) return false;

  Sentry.addBreadcrumb({
    category: 'fetch',
    level: 'warning',
    message: resolved.title,
    data: {
      status: resolved.status,
      method: resolved.method,
      endpoint: resolved.endpoint,
      failureCount,
    },
  });
  return true;
}

function delay(failureCount: number): number {
  const base = failureCount === 0 ? 500 : 1_500;
  return Math.round(base * (1 + Math.random() * 0.2));
}

async function run<T>(task: () => Promise<T>): Promise<T> {
  let failureCount = 0;
  for (;;) {
    try {
      return await task();
    } catch (error) {
      if (!retry(failureCount, error)) throw error;
      await new Promise((resolve) => setTimeout(resolve, delay(failureCount)));
      failureCount += 1;
    }
  }
}

export const globalRetry = { timeoutMs: TIMEOUT_MS, signal, retry, delay, run };
